import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Printer, RefreshCw, MapPin, Truck, Wallet, ChevronRight, Filter, X, Loader2 } from "lucide-react";
import { api, inr } from "../../lib/api";
import { toast } from "sonner";
import QuickPauseChip from "../../components/QuickPauseChip";
import { playNotifSound } from "../../lib/notifSound";
import { raiseOrderAlert, userWantsPersistentAlerts } from "../../lib/orderAlert";
import { EVENT_NEW_ORDER, MODE_DISABLED, MODE_FULL_SCREEN, resolveRule } from "../../lib/notificationRules";
import { useTenant } from "../../contexts/TenantContext";
import { useAuth } from "../../contexts/AuthContext";
import { useKeyedSubmitGuard } from "../../lib/useSubmitGuard";

const STATUS_PILLS = [
    { key: "", label: "All" },
    { key: "PLACED", label: "Placed" },
    { key: "CONFIRMED", label: "Confirmed" },
    { key: "PREPARING", label: "Preparing" },
    { key: "READY", label: "Ready" },
    { key: "OUT_FOR_DELIVERY", label: "On the way" },
    { key: "DELIVERED", label: "Delivered" },
    { key: "CANCELLED", label: "Cancelled" },
];

function todayIso() { return new Date().toISOString().slice(0, 10); }
function yesterdayIso() { const d = new Date(); d.setDate(d.getDate() - 1); return d.toISOString().slice(0, 10); }

export default function MerchantOrders() {
    const [rows, setRows] = useState([]);
    const [restaurants, setRestaurants] = useState([]);
    const [restaurantId, setRestaurantId] = useState("");
    const [status, setStatus] = useState("");
    const [datePreset, setDatePreset] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [showFilters, setShowFilters] = useState(false);
    const [printPreview, setPrintPreview] = useState(null);

    useEffect(() => { reloadRestaurants(); }, []);
    const reloadRestaurants = () => api.get("/merchant/restaurants").then(({ data }) => setRestaurants(data || []));

    const params = useMemo(() => {
        const p = new URLSearchParams();
        if (status) p.set("status", status);
        if (restaurantId) p.set("restaurant_id", restaurantId);
        if (dateFrom) p.set("date_from", dateFrom);
        if (dateTo) p.set("date_to", dateTo);
        return p.toString();
    }, [status, restaurantId, dateFrom, dateTo]);

    // Track the freshest order id we've seen so the polling tick can
    // detect a brand-new PLACED order and chime. We don't compare row
    // counts (orders can move OUT of the visible filter, e.g. a status
    // change) — comparing the max ``created_at`` is robust.
    const lastSeenAt = useRef(null);
    const { tenant } = useTenant() || {};
    const { user } = useAuth() || {};
    // How this tenant configured "New order" (Admin -> Notifications). With no rule saved this resolves to the
    // legacy behaviour, so nothing changes for a tenant that never opens that page.
    const orderRule = useRef(null);
    orderRule.current = resolveRule(tenant, EVENT_NEW_ORDER);
    const alertsOn = useRef(false);
    alertsOn.current = orderRule.current.mode === MODE_FULL_SCREEN && userWantsPersistentAlerts(user);

    const load = async () => {
        try {
            const { data } = await api.get(`/merchant/orders${params ? `?${params}` : ""}`);
            setRows(data);
            // Chime only when a NEW order arrives whose ``created_at``
            // is strictly later than the last one we'd already seen.
            // Skip the very first load (lastSeenAt === null) so the
            // page doesn't beep on every fresh page open.
            const newest = (data || []).reduce((m, r) => {
                const t = r?.created_at;
                return t && (!m || t > m) ? t : m;
            }, null);
            if (lastSeenAt.current && newest && newest > lastSeenAt.current) {
                const fresh = (data || []).filter((r) => r?.created_at > lastSeenAt.current && r.status === "PLACED");
                // Same persistent alert a push raises (keyed by order, so the two never double up).
                const rule = orderRule.current;
                if (alertsOn.current && fresh.length) {
                    fresh.forEach((r) => raiseOrderAlert({
                        key: `new_order:${r.id}`, kind: "new_order", orderId: r.id,
                        orderNumber: r.order_unique_id || r.id.slice(0, 8),
                        customerName: r.customer_name || "", amount: r.totals?.grand_total ?? "",
                        itemsCount: r.items?.length || "", body: "", path: `/merchant/orders/${r.id}`,
                        repeat: rule.repeat, sound: rule.sound,
                    }));
                } else if (rule.mode !== MODE_DISABLED && rule.sound) {
                    // Switched off, or set to deliver silently: no chime either — the refresh must not say
                    // something the push wouldn't.
                    playNotifSound();
                }
            }
            if (newest) lastSeenAt.current = newest;
        } catch (err) { console.error("[MerchantOrders] load failed:", err); }
    };
    useEffect(() => { load(); const i = setInterval(load, 6000); return () => clearInterval(i); /* eslint-disable-next-line */ }, [params]);

    const setDateShortcut = (kind) => {
        setDatePreset(kind);
        if (kind === "today") { const d = todayIso(); setDateFrom(d); setDateTo(d); }
        else if (kind === "yesterday") { const d = yesterdayIso(); setDateFrom(d); setDateTo(d); }
        else if (kind === "") { setDateFrom(""); setDateTo(""); }
    };

    // Bug found in production: this list's action buttons (Confirm,
    // etc.) had ZERO click protection — a double-click (or a stray
    // duplicate touch event) fired two PATCH requests, each triggering
    // its own order-status-change push notification. Guarded per
    // (order, action) so confirming one order doesn't lock the button
    // on an unrelated order — see useSubmitGuard.js for why a ref-based
    // synchronous check is needed instead of relying on state alone.
    const [actionBusyKey, runAction] = useKeyedSubmitGuard();
    const advance = (oid, next) => runAction(`${oid}:${next}`, async () => {
        try { await api.patch(`/merchant/orders/${oid}/status?status=${next}`); toast.success(`→ ${next.replace("_", " ")}`); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    });

    const printOrder = async (oid, kind) => {
        try { const { data } = await api.post(`/merchant/print/${oid}?kind=${kind}`); setPrintPreview(data); toast.success(`${kind} printed`); }
        catch (e) { toast.error(e?.response?.data?.detail || "Print failed"); }
    };

    const restById = useMemo(() => Object.fromEntries(restaurants.map(r => [r.id, r])), [restaurants]);
    const filterCount = (restaurantId ? 1 : 0) + (datePreset ? 1 : 0);

    return (
        <div>
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div><h1 className="text-2xl font-bold">Live Orders</h1><p className="text-sm text-muted-foreground mt-1">{rows.length} in view • auto-refresh 6s</p></div>
                <div className="flex items-center gap-2 flex-wrap">
                    <QuickPauseChip restaurants={restaurants} restaurantId={restaurantId} onChanged={reloadRestaurants} />
                    <button data-testid="orders-filters-toggle" onClick={() => setShowFilters(v => !v)}
                        className={`inline-flex items-center gap-2 text-xs font-semibold px-3 py-2 rounded-xl border ${filterCount ? "bg-primary/10 text-primary border-primary/30" : "border-border"}`}>
                        <Filter size={14} /> Filters{filterCount > 0 && <span className="bg-primary text-primary-foreground px-1.5 rounded-full text-[10px]">{filterCount}</span>}
                    </button>
                    <button onClick={load} className="text-xs font-semibold inline-flex items-center gap-1 text-primary"><RefreshCw size={12} /> Refresh</button>
                </div>
            </div>

            <div className="mt-4 flex items-center gap-2 flex-wrap">
                {STATUS_PILLS.map((s) => (
                    <button key={s.key || "all"} data-testid={`ord-${s.key || "ALL"}`} onClick={() => setStatus(s.key)}
                        className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${status === s.key ? "bg-foreground text-background border-foreground" : "border-border"}`}>{s.label}</button>
                ))}
            </div>

            {showFilters && (
                <div className="mt-4 bg-card border border-border rounded-2xl p-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                    <label className="flex flex-col gap-1">
                        <span className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Shop</span>
                        <select data-testid="filter-restaurant" className="inp" value={restaurantId} onChange={(e) => setRestaurantId(e.target.value)}>
                            <option value="">All my restaurants</option>
                            {restaurants.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                        </select>
                    </label>
                    <div className="flex flex-col gap-1 sm:col-span-2">
                        <span className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Date</span>
                        <div className="flex items-center gap-2 flex-wrap">
                            {[{ k: "", l: "Any" }, { k: "today", l: "Today" }, { k: "yesterday", l: "Yesterday" }, { k: "custom", l: "Custom range" }].map(opt => (
                                <button key={opt.k || "any"} onClick={() => setDateShortcut(opt.k)}
                                    className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${datePreset === opt.k ? "bg-foreground text-background border-foreground" : "border-border"}`}>{opt.l}</button>
                            ))}
                            {datePreset === "custom" && (
                                <div className="inline-flex items-center gap-2">
                                    <input type="date" className="inp max-w-[160px]" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                                    <span className="text-muted-foreground">to</span>
                                    <input type="date" className="inp max-w-[160px]" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                                </div>
                            )}
                        </div>
                    </div>
                    {filterCount > 0 && <button onClick={() => { setRestaurantId(""); setDateShortcut(""); }} className="inline-flex items-center gap-1 text-xs font-semibold text-destructive sm:col-span-3"><X size={12} /> Clear all</button>}
                    <style>{`.inp{background:hsl(var(--muted));border-radius:0.75rem;padding:0.55rem 0.75rem;outline:none;font-size:0.875rem;width:100%}`}</style>
                </div>
            )}

            <div className="mt-6 space-y-3">
                {rows.map((o) => {
                    const nextActions = Array.isArray(o.next_actions) ? o.next_actions : [];
                    const rest = restById[o.restaurant_id];
                    const cdc = !!(o.cdc_enabled ?? rest?.cdc_enabled);
                    return (
                        <div key={o.id} data-testid={`mord-${o.id}`} className="bg-card border border-border rounded-2xl p-4">
                            <div className="flex items-center gap-3 flex-wrap">
                                <div className="flex-1 min-w-[200px]">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <p className="font-bold text-sm">{o.order_unique_id}</p>
                                        <StatusBadge status={o.status} />
                                        {o.zone_name && <span data-testid={`zone-badge-${o.id}`} className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 inline-flex items-center gap-1"><MapPin size={10} />{o.zone_name}</span>}
                                        {cdc && <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-violet-100 text-violet-700">CDC</span>}
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-1">{o.customer_name || o.customer_mobile || "Customer"} • {o.restaurant_name}</p>
                                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{(o.items || []).map((i) => `${i.name} ×${i.quantity}`).join(", ")}</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-sm font-bold">{inr(o.totals?.grand_total)}</p>
                                    {cdc ? (
                                        <p className="text-[11px] text-violet-700 font-semibold inline-flex items-center gap-1"><Truck size={11} /> Driver pays you {inr(o.totals?.merchant_payout || 0)}</p>
                                    ) : o.payment_method === "COD" ? (
                                        <p className="text-[11px] text-amber-700 font-semibold inline-flex items-center gap-1"><Wallet size={11} /> Driver collects {inr(o.totals?.grand_total || 0)}</p>
                                    ) : (
                                        <p className="text-[11px] text-emerald-700 font-semibold">Prepaid online</p>
                                    )}
                                </div>
                                <div className="flex items-center gap-1">
                                    <button data-testid={`print-kot-${o.id}`} onClick={() => printOrder(o.id, "KOT")} title="Print KOT" className="text-xs font-semibold border border-border px-2 py-1.5 rounded-lg inline-flex items-center gap-1"><Printer size={12} /> KOT</button>
                                    <button data-testid={`print-bill-${o.id}`} onClick={() => printOrder(o.id, "BILL")} title="Print Bill" className="text-xs font-semibold border border-border px-2 py-1.5 rounded-lg inline-flex items-center gap-1"><Printer size={12} /> Bill</button>
                                </div>
                                <Link to={`/merchant/orders/${o.id}`} data-testid={`mord-open-${o.id}`} className="text-xs font-bold text-primary inline-flex items-center gap-1">Details <ChevronRight size={12} /></Link>
                            </div>
                            {nextActions.length > 0 && (
                                <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-border">
                                    {nextActions.map((act) => {
                                        const isCancel = act.slug === "CANCELLED" || act.slug === "REJECTED";
                                        const isBusy = actionBusyKey === `${o.id}:${act.slug}`;
                                        return (
                                            <button
                                                key={act.slug}
                                                data-testid={`mord-action-${o.id}-${act.slug}`}
                                                disabled={isBusy}
                                                onClick={() => advance(o.id, act.slug)}
                                                className={`rounded-xl px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-50 ${isCancel ? "bg-rose-100 text-rose-700 border border-rose-200" : "text-white"}`}
                                                style={isCancel ? undefined : { background: act.color }}
                                                title={`Move to ${act.label}`}
                                            >
                                                {isBusy && <Loader2 size={12} className="animate-spin" />}
                                                {isCancel ? `✕ ${act.label}` : `→ ${act.label}`}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
                {rows.length === 0 && <p className="text-center py-12 text-muted-foreground">No orders match these filters.</p>}
            </div>

            {printPreview && (
                <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setPrintPreview(null)}>
                    <div className="bg-white text-black rounded-xl p-4 font-mono text-xs whitespace-pre max-w-md w-full" data-testid="print-preview" onClick={(e) => e.stopPropagation()}>
                        {printPreview.content}
                        <button onClick={() => setPrintPreview(null)} className="mt-3 w-full bg-foreground text-background rounded-lg py-2 font-bold text-sm">Close</button>
                    </div>
                </div>
            )}
        </div>
    );
}

function StatusBadge({ status }) {
    const map = {
        DELIVERED: "bg-emerald-100 text-emerald-700",
        CANCELLED: "bg-red-100 text-red-700",
        REJECTED: "bg-red-100 text-red-700",
        PLACED: "bg-amber-100 text-amber-700",
        CONFIRMED: "bg-amber-100 text-amber-700",
        PREPARING: "bg-orange-100 text-orange-700",
        READY: "bg-blue-100 text-blue-700",
        PICKED_UP: "bg-indigo-100 text-indigo-700",
        OUT_FOR_DELIVERY: "bg-indigo-100 text-indigo-700",
    };
    const cls = map[status] || "bg-muted text-muted-foreground";
    return <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${cls}`}>{(status || "").replace("_", " ")}</span>;
}
